import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ViewStyle } from 'react-native';
import { PanResponder, Pressable, View } from 'react-native';
import Svg, { Circle, Line as SvgLine, Path } from 'react-native-svg';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { Card, Text } from '@/ui';

const PLOT_HEIGHT = 168;
const Y_AXIS_WIDTH = 36;

type LineChartPoint = {
  label: string;
  detailLabel?: string;
  value: number | null;
};

type LineChartLayout = 'default' | 'compact';

type LineChartProps = {
  points: LineChartPoint[];
  formatValue: (value: number) => string;
  formatAxis: (value: number) => string;
  strokeColor: string;
  minValue?: number;
  maxValue?: number;
  /** Y-axis ticks at whole steps (e.g. 3.0, 4.0, 5.0) when min/max are set. */
  integerAxisTicks?: boolean;
  layout?: LineChartLayout;
};

const COMPACT_TOOLTIP_WIDTH = 80;
const SCRUB_LONG_PRESS_MS = 200;

function selectedIndexFromX(x: number, width: number, count: number) {
  if (count <= 0) {
    return null;
  }

  if (count === 1) {
    return 0;
  }

  const clampedX = Math.min(Math.max(x, 0), width);

  return Math.round((clampedX / width) * (count - 1));
}

function shouldShowCompactXLabel(index: number, count: number) {
  if (count <= 1) {
    return true;
  }

  if (index === 0 || index === count - 1) {
    return true;
  }

  const anchors = new Set(
    [0, 0.25, 0.5, 0.75, 1].map((fraction) =>
      Math.round(fraction * (count - 1))
    )
  );

  return anchors.has(index);
}

function axisBounds(values: number[], minValue?: number, maxValue?: number) {
  const active = values.filter((value) => Number.isFinite(value));
  if (active.length === 0) {
    return { min: minValue ?? 0, max: maxValue ?? 1 };
  }

  const dataMin = Math.min(...active);
  const dataMax = Math.max(...active);
  const min = minValue ?? Math.floor(dataMin * 4) / 4;
  const max = maxValue ?? Math.ceil(dataMax * 4) / 4;

  if (min === max) {
    return { min: min - 0.25, max: max + 0.25 };
  }

  return { min, max };
}

function chartNumericValue(value: number | null | undefined) {
  if (value == null || !Number.isFinite(value)) {
    return null;
  }

  return value;
}

function linePathWithGaps(points: { x: number; y: number | null }[]) {
  let path = '';
  let segmentOpen = false;

  for (const point of points) {
    if (point.y === null) {
      segmentOpen = false;
      continue;
    }

    if (!segmentOpen) {
      path += `${path ? ' ' : ''}M ${point.x} ${point.y}`;
      segmentOpen = true;
      continue;
    }

    path += ` L ${point.x} ${point.y}`;
  }

  return path;
}

function yAxisTicks({
  min,
  max,
  integerAxisTicks,
  isCompactLayout,
}: {
  min: number;
  max: number;
  integerAxisTicks: boolean;
  isCompactLayout: boolean;
}) {
  if (isCompactLayout) {
    return [min];
  }

  if (integerAxisTicks) {
    const lo = Math.floor(min);
    const hi = Math.ceil(max);

    if (hi <= lo) {
      return [hi];
    }

    if (hi - lo === 1) {
      return [hi, lo];
    }

    const mid = lo + Math.floor((hi - lo) / 2);
    return [hi, mid, lo];
  }

  const range = max - min || 1;

  return [max, min + range / 2, min];
}

export function LineChart({
  points,
  formatValue,
  formatAxis,
  strokeColor,
  minValue,
  maxValue,
  integerAxisTicks = false,
  layout = 'default',
}: LineChartProps) {
  const { styles, theme } = useStyles(stylesheet);
  const isCompactLayout = layout === 'compact';
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [plotWidth, setPlotWidth] = useState(0);
  const scrubbingRef = useRef(false);
  const scrubGrantXRef = useRef(0);
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearLongPressTimer = useCallback(() => {
    if (longPressTimerRef.current !== null) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const updateSelectionFromX = useCallback(
    (x: number) => {
      if (plotWidth <= 0 || points.length === 0) {
        return;
      }

      setSelectedIndex(selectedIndexFromX(x, plotWidth, points.length));
    },
    [plotWidth, points.length]
  );

  const compactScrubResponder = useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onMoveShouldSetPanResponder: () => scrubbingRef.current,
        onPanResponderGrant: (event) => {
          const x = event.nativeEvent.locationX;
          scrubGrantXRef.current = x;
          clearLongPressTimer();
          longPressTimerRef.current = setTimeout(() => {
            scrubbingRef.current = true;
            updateSelectionFromX(x);
          }, SCRUB_LONG_PRESS_MS);
        },
        onPanResponderMove: (event) => {
          const x = event.nativeEvent.locationX;

          if (scrubbingRef.current) {
            updateSelectionFromX(x);
            return;
          }

          if (
            longPressTimerRef.current !== null &&
            Math.abs(x - scrubGrantXRef.current) > 10
          ) {
            clearLongPressTimer();
          }
        },
        onPanResponderRelease: () => {
          clearLongPressTimer();
          scrubbingRef.current = false;
          setSelectedIndex(null);
        },
        onPanResponderTerminate: () => {
          clearLongPressTimer();
          scrubbingRef.current = false;
          setSelectedIndex(null);
        },
      }),
    [clearLongPressTimer, updateSelectionFromX]
  );

  useEffect(
    () => () => {
      clearLongPressTimer();
    },
    [clearLongPressTimer]
  );

  const numericValues = points
    .map((point) => chartNumericValue(point.value))
    .filter((value): value is number => value !== null);
  const { min, max } = axisBounds(numericValues, minValue, maxValue);
  const range = max - min || 1;
  const ticks = yAxisTicks({
    min,
    max,
    integerAxisTicks,
    isCompactLayout,
  });
  const selected = selectedIndex === null ? null : points[selectedIndex];

  const coordinates = points.map((point, index) => {
    const plotValue = chartNumericValue(point.value);
    const x =
      points.length <= 1
        ? plotWidth / 2
        : (index / (points.length - 1)) * plotWidth;
    const y =
      plotValue === null
        ? null
        : PLOT_HEIGHT - ((plotValue - min) / range) * PLOT_HEIGHT;

    return { x, y, point, index };
  });

  const lineGeometry = useMemo(() => {
    if (plotWidth <= 0 || points.length === 0) {
      return { pathD: '' };
    }

    const drawablePoints = points.map((point, index) => {
      const plotValue = chartNumericValue(point.value);
      const x =
        points.length <= 1
          ? plotWidth / 2
          : (index / (points.length - 1)) * plotWidth;
      const y =
        plotValue === null
          ? null
          : PLOT_HEIGHT - ((plotValue - min) / range) * PLOT_HEIGHT;

      return { x, y };
    });

    return {
      pathD: linePathWithGaps(drawablePoints),
    };
  }, [min, plotWidth, points, range]);

  const selectedCoordinate =
    selectedIndex === null ? null : coordinates[selectedIndex];

  const tooltipLeft =
    selectedIndex === null || plotWidth === 0
      ? 0
      : Math.min(
          Math.max((selectedCoordinate?.x ?? 0) - COMPACT_TOOLTIP_WIDTH / 2, 0),
          Math.max(plotWidth - COMPACT_TOOLTIP_WIDTH, 0)
        );

  const compactTooltipTop =
    selectedCoordinate?.y === null || selectedCoordinate?.y === undefined
      ? 0
      : Math.min(
          Math.max(selectedCoordinate.y - 28, 0),
          Math.max(PLOT_HEIGHT - 56, 0)
        );

  return (
    <View style={styles.chart}>
      <View style={styles.plotRow}>
        {isCompactLayout ? null : (
          <View style={styles.yAxis}>
            {ticks.map((tick) => (
              <Text key={tick} type="caption2" style={styles.axisLabel}>
                {formatAxis(tick)}
              </Text>
            ))}
          </View>
        )}

        <View
          style={styles.plot}
          onLayout={(event) => {
            setPlotWidth(event.nativeEvent.layout.width);
          }}
        >
          {(isCompactLayout ? [min] : ticks).map((tick) => (
            <View
              key={tick}
              style={[
                styles.gridline,
                { bottom: ((tick - min) / range) * PLOT_HEIGHT },
              ]}
            />
          ))}

          {plotWidth > 0 && lineGeometry.pathD ? (
            <Svg
              width={plotWidth}
              height={PLOT_HEIGHT}
              style={styles.svg}
              pointerEvents="none"
            >
              <Path
                d={lineGeometry.pathD}
                fill="none"
                stroke={strokeColor}
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {selectedCoordinate?.y != null && selected?.value != null ? (
                <>
                  {isCompactLayout ? (
                    <SvgLine
                      x1={selectedCoordinate.x}
                      x2={selectedCoordinate.x}
                      y1={0}
                      y2={PLOT_HEIGHT}
                      stroke={theme.colors.card.default.borderColor}
                      strokeWidth={1}
                      strokeDasharray="4 4"
                    />
                  ) : null}
                  <Circle
                    cx={selectedCoordinate.x}
                    cy={selectedCoordinate.y}
                    r={5}
                    fill={strokeColor}
                    stroke={theme.colors.background_2}
                    strokeWidth={2}
                  />
                </>
              ) : null}
            </Svg>
          ) : null}

          {isCompactLayout ? (
            <View
              style={styles.hitTargets}
              {...compactScrubResponder.panHandlers}
            />
          ) : (
            <View style={styles.hitTargets}>
              {coordinates.map((item) => (
                <Pressable
                  key={`${item.point.label}-${item.index}`}
                  style={styles.hitColumn}
                  onHoverIn={() => setSelectedIndex(item.index)}
                  onHoverOut={() => {
                    setSelectedIndex((current) =>
                      current === item.index ? null : current
                    );
                  }}
                  onPress={() =>
                    setSelectedIndex((current) =>
                      current === item.index ? null : item.index
                    )
                  }
                />
              ))}
            </View>
          )}

          {selected && selected.value !== null ? (
            <View
              pointerEvents="none"
              style={[
                isCompactLayout ? styles.compactTooltip : styles.tooltip,
                {
                  left: tooltipLeft,
                  ...(isCompactLayout ? { top: compactTooltipTop } : undefined),
                } as ViewStyle,
              ]}
            >
              {isCompactLayout ? (
                <View style={styles.compactTooltipCard}>
                  <Text type="caption1" style={styles.axisLabel}>
                    {selected.detailLabel ?? selected.label}
                  </Text>
                  <Text type="bodyRegular">{formatValue(selected.value)}</Text>
                </View>
              ) : (
                <Card style={styles.tooltipCard}>
                  <Text type="caption1" style={styles.axisLabel}>
                    {selected.label}
                  </Text>
                  <Text type="bodyBold">{formatValue(selected.value)}</Text>
                </Card>
              )}
            </View>
          ) : null}
        </View>
      </View>

      <View style={styles.xAxis}>
        {isCompactLayout ? (
          <View style={styles.compactXLabels}>
            {points.map((point, index) =>
              shouldShowCompactXLabel(index, points.length) ? (
                <Text
                  key={`${point.label}-${index}`}
                  type="caption1"
                  style={styles.compactXLabel}
                >
                  {point.label}
                </Text>
              ) : null
            )}
          </View>
        ) : (
          <>
            <View style={styles.yAxisSpacer} />
            <View style={styles.xLabels}>
              {points.map((point, index) => (
                <View key={`${point.label}-${index}`} style={styles.xLabelCell}>
                  <Text type="caption2" style={styles.xLabel} numberOfLines={1}>
                    {point.label}
                  </Text>
                </View>
              ))}
            </View>
          </>
        )}
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  chart: {
    gap: 8,
  },
  plotRow: {
    flexDirection: 'row',
    gap: 8,
  },
  yAxis: {
    width: Y_AXIS_WIDTH,
    height: PLOT_HEIGHT,
    justifyContent: 'space-between',
  },
  yAxisSpacer: {
    width: Y_AXIS_WIDTH + 8,
  },
  axisLabel: {
    color: theme.colors.text.secondary,
  },
  plot: {
    flex: 1,
    height: PLOT_HEIGHT,
  },
  gridline: {
    position: 'absolute',
    left: 0,
    right: 0,
    height: 1,
    backgroundColor: theme.colors.card.default.borderColor,
  },
  svg: {
    position: 'absolute',
    left: 0,
    top: 0,
  },
  hitTargets: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    height: PLOT_HEIGHT,
    flexDirection: 'row',
  },
  hitColumn: {
    flex: 1,
    height: '100%',
  },
  xAxis: {
    flexDirection: 'row',
  },
  xLabels: {
    flex: 1,
    flexDirection: 'row',
  },
  xLabelCell: {
    flex: 1,
    minWidth: 0,
  },
  compactXLabels: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 4,
  },
  xLabel: {
    textAlign: 'center',
    color: theme.colors.text.secondary,
  },
  compactXLabel: {
    flexShrink: 0,
    color: theme.colors.text.secondary,
  },
  tooltip: {
    position: 'absolute',
    top: 0,
    width: COMPACT_TOOLTIP_WIDTH,
    zIndex: 2,
  },
  compactTooltip: {
    position: 'absolute',
    width: COMPACT_TOOLTIP_WIDTH,
    zIndex: 2,
  },
  compactTooltipCard: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 2,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.card.hover.borderColor,
    backgroundColor: theme.colors.card.hover.backgroundColor,
    alignItems: 'center',
  },
  tooltipCard: {
    padding: 12,
    gap: 4,
  },
}));
